import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2, ArrowLeft } from "lucide-react";
import { api } from "../../lib/api";

const empty = {
  title: "",
  slug: "",
  excerpt: "",
  content: "",
  cover_image: "",
  category: "Guide",
  published: true,
  schema_faq: [],
};

const slugify = (s) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

export default function PostEditorPage() {
  const { slug } = useParams();
  const isNew = !slug;
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isNew) return;
    api
      .getBlogPost(slug)
      .then((p) =>
        setForm({
          title: p.title || "",
          slug: p.slug || "",
          excerpt: p.excerpt || "",
          content: p.content || "",
          cover_image: p.cover_image || "",
          category: p.category || "Guide",
          published: !!p.published,
          schema_faq: p.schema_faq || [],
        }),
      )
      .finally(() => setLoading(false));
  }, [slug, isNew]);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...form,
        slug: form.slug || slugify(form.title),
      };
      if (isNew) {
        await api.adminCreatePost(payload);
      } else {
        await api.adminUpdatePost(slug, payload);
      }
      navigate("/admin");
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="container-mps py-20 text-[#6B7280]">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-[#F8F5F0] text-[#1A1A1A]">
      <header className="bg-[#0A0A0A] text-[#F8F5F0]">
        <div className="container-mps flex items-center justify-between h-20">
          <Link to="/admin" className="inline-flex items-center gap-2 overline text-[#C9A96E]">
            <ArrowLeft size={14} /> Back to Dashboard
          </Link>
          <span className="font-serif text-2xl text-[#C9A96E]">
            {isNew ? "New Post" : "Edit Post"}
          </span>
        </div>
      </header>

      <form onSubmit={onSubmit} className="container-mps max-w-4xl mx-auto py-12 space-y-6" data-testid="post-editor-form">
        <Field label="Title">
          <input
            data-testid="post-editor-title"
            required
            value={form.title}
            onChange={(e) => update("title", e.target.value)}
            className="input-admin"
          />
        </Field>
        <Field label="Slug (auto-generated if blank)">
          <input
            data-testid="post-editor-slug"
            value={form.slug}
            onChange={(e) => update("slug", e.target.value)}
            placeholder={slugify(form.title)}
            className="input-admin"
          />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <Field label="Category">
            <input
              data-testid="post-editor-category"
              value={form.category}
              onChange={(e) => update("category", e.target.value)}
              className="input-admin"
            />
          </Field>
          <Field label="Cover Image URL">
            <input
              data-testid="post-editor-cover"
              value={form.cover_image}
              onChange={(e) => update("cover_image", e.target.value)}
              className="input-admin"
            />
          </Field>
        </div>
        <Field label="Excerpt">
          <textarea
            data-testid="post-editor-excerpt"
            rows={2}
            value={form.excerpt}
            onChange={(e) => update("excerpt", e.target.value)}
            className="input-admin"
          />
        </Field>
        <Field label="Content (markdown-lite: ## headings, **bold**, [text](url))">
          <textarea
            data-testid="post-editor-content"
            rows={20}
            value={form.content}
            onChange={(e) => update("content", e.target.value)}
            className="input-admin font-mono text-sm"
          />
        </Field>
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            data-testid="post-editor-published"
            checked={form.published}
            onChange={(e) => update("published", e.target.checked)}
            className="h-4 w-4 accent-[#C9A96E]"
          />
          <span className="text-sm">Published (visible on /blog)</span>
        </label>

        {error && (
          <div className="px-4 py-3 bg-[#0A0A0A] text-[#F8F5F0] text-sm border-l-2 border-[#C9A96E]">
            {String(error)}
          </div>
        )}

        <div className="flex gap-4">
          <button
            type="submit"
            disabled={saving}
            data-testid="post-editor-save"
            className="inline-flex items-center gap-2 px-8 py-3 bg-[#0A0A0A] text-[#F8F5F0] text-sm uppercase tracking-[0.16em] hover:bg-[#1A1A1A] disabled:opacity-60"
          >
            {saving && <Loader2 size={16} className="animate-spin" />} Save
          </button>
          <Link
            to="/admin"
            className="inline-flex items-center px-8 py-3 border border-[#E5E0D8] text-sm uppercase tracking-[0.16em]"
          >
            Cancel
          </Link>
        </div>
      </form>

      <style>{`
        .input-admin {
          width: 100%;
          padding: 0.75rem 1rem;
          background: #fff;
          border: 1px solid #E5E0D8;
          color: #1A1A1A;
          font-size: 1rem;
          outline: none;
        }
        .input-admin:focus { border-color: #C9A96E; }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="overline text-[#1A1A1A] mb-2 block">{label}</span>
      {children}
    </label>
  );
}
